import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { ArrowRight, HelpCircle, Sparkles } from "lucide-react";

const FAQ = () => {
  const { language, t } = useLanguage();
  const isGerman = language === "de";

  const faqCategories = isGerman
    ? [
        {
          title: "Allgemeine Fragen",
          items: [
            {
              q: "Was ist Fuel72?",
              a: "Fuel72 ist ein AI Video-Produktionsunternehmen (KEIN Brennstoff-Produkt). Wir erstellen professionelle UGC Videos mit künstlicher Intelligenz für Unternehmen weltweit. In nur 72 Stunden erhalten Sie 6 Videos + 6 Poster + Captions.",
            },
            {
              q: "Was bedeutet UGC?",
              a: "UGC steht für 'User Generated Content' - authentischer Content, der wie von echten Nutzern erstellt aussieht. Mit Fuel72 erstellen wir diesen Content mit AI, ohne dass Sie selbst filmen oder Influencer engagieren müssen.",
            },
            {
              q: "Wie unterscheidet sich AI UGC von traditionellem UGC?",
              a: "Traditionelles UGC erfordert echte Creator, ist teuer (500-2000€ pro Creator) und dauert Wochen. AI UGC von Fuel72 ist günstiger, schneller (72 Stunden) und unbegrenzt skalierbar.",
            },
            {
              q: "Für welche Branchen eignet sich Fuel72?",
              a: "Fuel72 eignet sich für alle Branchen: Restaurants, Fitness Studios, Beauty Salons, E-Commerce, SaaS, lokale Geschäfte, Mode, Immobilien und mehr.",
            },
          ],
        },
        {
          title: "Preise & Pakete",
          items: [
            {
              q: "Was kostet Fuel72?",
              a: "Unser Launch Pack kostet 297€ und beinhaltet 6 AI UGC Videos, 6 Poster und Captions. Keine versteckten Gebühren, keine Abos.",
            },
            {
              q: "Gibt es verschiedene Pakete?",
              a: "Ja! Starter Pack (149€ für 3 Videos), Launch Pack (297€ für 6 Videos) und Scale Pack (497€ für 12 Videos). Alle Pakete beinhalten Poster und Captions.",
            },
            {
              q: "Gibt es Add-ons?",
              a: "Ja, optionale Add-ons: Express Delivery (24h statt 72h), Extra Revisionen, Voiceover in verschiedenen Sprachen, und mehr.",
            },
            {
              q: "Kann ich vor dem Kauf Beispiele sehen?",
              a: "Absolut! Besuchen Sie unsere Homepage und scrollen Sie zu unserem Portfolio mit echten Beispielen für verschiedene Branchen.",
            },
          ],
        },
        {
          title: "Lieferung & Prozess",
          items: [
            {
              q: "Wie lange dauert die Lieferung?",
              a: "Standard-Lieferung ist 72 Stunden. Mit Express Delivery erhalten Sie Ihre Videos in nur 24 Stunden.",
            },
            {
              q: "Wie funktioniert der Bestellprozess?",
              a: "1) Füllen Sie unser Brief-Formular aus (Logo, Farben, Zielgruppe). 2) Wir erstellen Ihre Videos mit AI. 3) Sie erhalten die fertigen Videos per Email.",
            },
            {
              q: "Kann ich Änderungen anfordern?",
              a: "Ja! Eine kostenlose Revisionsrunde ist inklusive. Weitere Revisionen können als Add-on gebucht werden.",
            },
            {
              q: "In welchem Format erhalte ich die Videos?",
              a: "MP4 in vertikalem Format (9:16), optimiert für TikTok, Instagram Reels, YouTube Shorts und Meta Ads.",
            },
          ],
        },
        {
          title: "Technische Fragen",
          items: [
            {
              q: "Brauche ich eine Kamera oder Ausrüstung?",
              a: "Nein! Das ist der Vorteil von AI UGC - Sie brauchen keine Kamera, kein Studio, keine Ausrüstung. Wir erstellen alles für Sie.",
            },
            {
              q: "Muss ich vor der Kamera stehen?",
              a: "Nein! Unsere AI erstellt authentischen Content ohne dass Sie selbst gefilmt werden müssen.",
            },
            {
              q: "Welche Sprachen werden unterstützt?",
              a: "Deutsch, Englisch, Spanisch, Französisch, Arabisch und weitere Sprachen auf Anfrage.",
            },
            {
              q: "Kann ich die Videos für Werbung nutzen?",
              a: "Ja! Alle Videos kommen mit vollen kommerziellen Nutzungsrechten für Social Media, Ads und Marketing.",
            },
          ],
        },
        {
          title: "Vergleich & Alternativen",
          items: [
            {
              q: "Fuel72 vs. traditionelle UGC Creator?",
              a: "Fuel72 ist 80% günstiger, 10x schneller und unbegrenzt skalierbar. Keine No-Shows, keine Qualitätsschwankungen.",
            },
            {
              q: "Fuel72 vs. Synthesia/Heygen?",
              a: "Fuel72 ist spezialisiert auf UGC-Style Content für Marketing. Wir liefern komplette Pakete mit Poster und Captions, nicht nur einzelne Videos.",
            },
            {
              q: "Warum sollte ich keine Influencer buchen?",
              a: "Influencer sind teuer, unzuverlässig und nicht skalierbar. Mit Fuel72 bekommen Sie konsistente Qualität zum Festpreis.",
            },
          ],
        },
      ]
    : [
        {
          title: "General Questions",
          items: [
            {
              q: "What is Fuel72?",
              a: "Fuel72 is an AI video production company (NOT a fuel product). We create professional UGC videos using artificial intelligence for businesses worldwide. Get 6 videos + 6 posters + captions in just 72 hours.",
            },
            {
              q: "What does UGC mean?",
              a: "UGC stands for 'User Generated Content' - authentic content that looks like it was created by real users. With Fuel72, we create this content with AI, without you filming or hiring influencers.",
            },
            {
              q: "How is AI UGC different from traditional UGC?",
              a: "Traditional UGC requires real creators, is expensive ($500-2000+ per creator) and takes weeks. AI UGC from Fuel72 is cheaper, faster (72 hours) and infinitely scalable.",
            },
            {
              q: "Which industries does Fuel72 work with?",
              a: "Fuel72 works with all industries: restaurants, fitness studios, beauty salons, e-commerce, SaaS, local businesses, fashion, real estate and more.",
            },
          ],
        },
        {
          title: "Pricing & Packages",
          items: [
            {
              q: "How much does Fuel72 cost?",
              a: "Our Launch Pack costs $297 and includes 6 AI UGC videos, 6 posters and captions. No hidden fees, no subscriptions.",
            },
            {
              q: "Are there different packages?",
              a: "Yes! Starter Pack ($149 for 3 videos), Launch Pack ($297 for 6 videos) and Scale Pack ($497 for 12 videos). All packages include posters and captions.",
            },
            {
              q: "Are there add-ons?",
              a: "Yes, optional add-ons: Express Delivery (24h instead of 72h), Extra Revisions, Voiceover in different languages, and more.",
            },
            {
              q: "Can I see examples before purchasing?",
              a: "Absolutely! Visit our homepage and scroll to our portfolio with real examples for different industries.",
            },
          ],
        },
        {
          title: "Delivery & Process",
          items: [
            {
              q: "How long does delivery take?",
              a: "Standard delivery is 72 hours. With Express Delivery you receive your videos in just 24 hours.",
            },
            {
              q: "How does the ordering process work?",
              a: "1) Fill out our brief form (logo, colors, target audience). 2) We create your videos with AI. 3) You receive the finished videos via email.",
            },
            {
              q: "Can I request changes?",
              a: "Yes! One free revision round is included. Additional revisions can be booked as an add-on.",
            },
            {
              q: "What format will I receive the videos in?",
              a: "MP4 in vertical format (9:16), optimized for TikTok, Instagram Reels, YouTube Shorts and Meta Ads.",
            },
          ],
        },
        {
          title: "Technical Questions",
          items: [
            {
              q: "Do I need a camera or equipment?",
              a: "No! That's the advantage of AI UGC - you don't need a camera, studio, or equipment. We create everything for you.",
            },
            {
              q: "Do I need to be on camera?",
              a: "No! Our AI creates authentic content without you having to be filmed.",
            },
            {
              q: "Which languages are supported?",
              a: "German, English, Spanish, French, Arabic and other languages on request.",
            },
            {
              q: "Can I use the videos for advertising?",
              a: "Yes! All videos come with full commercial usage rights for social media, ads and marketing.",
            },
          ],
        },
        {
          title: "Comparisons & Alternatives",
          items: [
            {
              q: "Fuel72 vs. traditional UGC creators?",
              a: "Fuel72 is 80% cheaper, 10x faster and infinitely scalable. No no-shows, no quality fluctuations.",
            },
            {
              q: "Fuel72 vs. Synthesia/Heygen?",
              a: "Fuel72 specializes in UGC-style content for marketing. We deliver complete packages with posters and captions, not just individual videos.",
            },
            {
              q: "Why shouldn't I hire influencers?",
              a: "Influencers are expensive, unreliable and not scalable. With Fuel72 you get consistent quality at a fixed price.",
            },
          ],
        },
      ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqCategories.flatMap((category) =>
      category.items.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a,
        },
      }))
    ),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.fuel72.com",
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "FAQ",
        "item": "https://www.fuel72.com/faq",
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>
          {isGerman
            ? "FAQ | Häufige Fragen zu AI UGC Videos | Fuel72"
            : "FAQ | Frequently Asked Questions About AI UGC Videos | Fuel72"}
        </title>
        <meta
          name="description"
          content={
            isGerman
              ? "Alle Antworten zu Fuel72 AI UGC: Preise, Lieferzeit, Prozess, Vergleich mit Influencern. UGC Videos erstellen ohne Filmen. 72 Stunden Lieferung."
              : "All answers about Fuel72 AI UGC: pricing, delivery time, process, comparison with influencers. Create UGC videos without filming. 72-hour delivery."
          }
        />
        <meta
          name="keywords"
          content={
            isGerman
              ? "fuel72 faq, ugc fragen, ai ugc kosten, ugc preise, ugc vs influencer, wie funktioniert ai ugc, fuel72 erfahrungen"
              : "fuel72 faq, ugc questions, ai ugc cost, ugc pricing, ugc vs influencer, how ai ugc works, fuel72 reviews"
          }
        />
        <link rel="canonical" href="https://www.fuel72.com/faq" />
        <link rel="alternate" hrefLang="en" href="https://www.fuel72.com/faq" />
        <link rel="alternate" hrefLang="de" href="https://www.fuel72.com/faq?lang=de" />
        <link rel="alternate" hrefLang="x-default" href="https://www.fuel72.com/faq" />
        <meta property="og:title" content={isGerman ? "FAQ | Fuel72 AI UGC" : "FAQ | Fuel72 AI UGC"} />
        <meta property="og:url" content="https://www.fuel72.com/faq" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <div className="min-h-screen gradient-mesh">
        <Navbar />

        <main className="container mx-auto px-4 py-20 md:py-28">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-neon-purple/10 border border-neon-purple/30 mb-6">
              <HelpCircle className="w-4 h-4 text-neon-purple" />
              <span className="text-sm font-medium text-neon-purple">
                {isGerman ? "Häufige Fragen" : "Frequently Asked Questions"}
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6">
              {isGerman ? (
                <>
                  Alles über{" "}
                  <span className="bg-gradient-to-r from-neon-purple via-neon-pink to-orange-500 bg-clip-text text-transparent">
                    AI UGC Videos
                  </span>
                </>
              ) : (
                <>
                  Everything About{" "}
                  <span className="bg-gradient-to-r from-neon-purple via-neon-pink to-orange-500 bg-clip-text text-transparent">
                    AI UGC Videos
                  </span>
                </>
              )}
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {isGerman
                ? "Finden Sie Antworten zu Preisen, Lieferzeiten, dem Bestellprozess und wie AI UGC Ihr Business transformieren kann."
                : "Find answers about pricing, delivery times, the ordering process and how AI UGC can transform your business."}
            </p>
          </div>

          {/* FAQ Categories */}
          <div className="max-w-4xl mx-auto space-y-8">
            {faqCategories.map((category, categoryIndex) => (
              <div
                key={categoryIndex}
                className="glass-morphism border border-white/10 rounded-2xl p-6 md:p-8"
              >
                <h2 className="text-2xl font-heading font-bold mb-6 flex items-center gap-3">
                  <Sparkles className="w-6 h-6 text-neon-purple" />
                  {category.title}
                </h2>
                <Accordion type="single" collapsible className="space-y-2">
                  {category.items.map((item, itemIndex) => (
                    <AccordionItem
                      key={itemIndex}
                      value={`item-${categoryIndex}-${itemIndex}`}
                      className="border-white/10"
                    >
                      <AccordionTrigger className="text-left hover:text-neon-purple transition-colors">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            ))}
          </div>

          {/* CTA Section */}
          <div className="text-center mt-16">
            <p className="text-lg text-muted-foreground mb-6">
              {isGerman
                ? "Haben Sie weitere Fragen? Kontaktieren Sie uns oder starten Sie jetzt."
                : "Have more questions? Contact us or get started now."}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-gradient-to-r from-neon-purple via-neon-pink to-orange-500 text-white font-bold px-8"
              >
                <Link to="/order">
                  {isGerman ? "Jetzt starten" : "Get Started"}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <a href="mailto:getfuel72@gmail.com">
                  {isGerman ? "Kontakt aufnehmen" : "Contact Us"}
                </a>
              </Button>
            </div>
          </div>

          {/* Internal Links for SEO */}
          <div className="mt-20 pt-10 border-t border-white/10">
            <h3 className="text-lg font-bold mb-4 text-center">
              {isGerman ? "Weitere Informationen" : "Learn More"}
            </h3>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Link to={isGerman ? "/ugc-kaufen" : "/buy-ugc"} className="text-neon-purple hover:underline">
                {isGerman ? "UGC kaufen" : "Buy UGC"}
              </Link>
              <Link to={isGerman ? "/ki-ugc-generator" : "/ai-ugc-video-generator"} className="text-neon-purple hover:underline">
                {isGerman ? "KI UGC Generator" : "AI UGC Generator"}
              </Link>
              <Link to="/blog" className="text-neon-purple hover:underline">
                Blog
              </Link>
              <Link to={isGerman ? "/ugc-agentur" : "/hire-ugc"} className="text-neon-purple hover:underline">
                {isGerman ? "UGC Agentur" : "Hire UGC"}
              </Link>
              <Link to="/order" className="text-neon-purple hover:underline">
                {isGerman ? "Bestellen" : "Order"}
              </Link>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default FAQ;